'use client'

import type { CSSProperties } from 'react'
import type { Ref } from 'react'
import { useMarqueeCycleCopies } from '@/components/ui/use-marquee-cycle-copies'
import { cn } from '@/lib/utils'

type PartnersTickerTypographyClass =
  | 'header-size-1'
  | 'header-size-2'
  | 'header-size-3'
  | 'header-size-4'

function TickerTrack({
  names,
  separator,
  trackRef,
  typographyClass,
}: {
  names: string[]
  separator: string
  trackRef?: Ref<HTMLDivElement>
  typographyClass?: PartnersTickerTypographyClass
}) {
  return (
    <div ref={trackRef} className="flex shrink-0 items-center" aria-hidden="true">
      {names.map((name, index) => (
        <div key={`${name}-${index}`} className="flex shrink-0 items-center">
          <span className={cn('whitespace-nowrap px-2 md:px-4', typographyClass)}>
            {name}
          </span>
          {separator && (
            <span
              aria-hidden="true"
              className={cn('whitespace-nowrap px-2 md:px-4', typographyClass)}
            >
              {separator}
            </span>
          )}
        </div>
      ))}
    </div>
  )
}

export function PartnersTickerMarquee({
  names,
  secondsPerPartner,
  separator,
  typographyClass,
}: {
  names: string[]
  secondsPerPartner: number
  separator: string
  typographyClass?: PartnersTickerTypographyClass
}) {
  const {
    containerRef,
    cycleCopies,
    cycleRef,
  } = useMarqueeCycleCopies({
    itemCount: names.length,
  })
  const tickerNames = Array.from({ length: cycleCopies }).flatMap(() => names)
  const duration = `${tickerNames.length * secondsPerPartner}s`

  return (
    <div ref={containerRef} className="flex overflow-hidden">
      <div
        className="flex w-max animate-image-carousel-marquee motion-reduce:animate-none"
        style={
          {
            '--image-carousel-marquee-duration': duration,
          } as CSSProperties
        }
      >
        {[0, 1].map((groupIndex) => (
          <TickerTrack
            key={groupIndex}
            names={tickerNames}
            separator={separator}
            trackRef={groupIndex === 0 ? cycleRef : undefined}
            typographyClass={typographyClass}
          />
        ))}
      </div>
    </div>
  )
}
